import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  FiUpload,
  FiRefreshCw,
  FiSend,
  FiLink,
  FiPlus,
  FiChevronDown,
  FiVideo,
  FiPlay,
  FiClock,
  FiFacebook,
  FiCopy,
} from "react-icons/fi";
import { MdClose } from "react-icons/md";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import PointWarning from "./components/PointWarning";
import { getDatabase, ref, get, update, set, push, onValue } from "firebase/database";
import SchedulePostModal from "./components/SchedulePostModal";
import ScheduledPostsList from "./components/ScheduledPostsList";
import {
  POST_LENGTHS,
  POST_STYLES,
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
} from "./constants/postSettings";
import PostLanguageSelector from "./components/PostLanguageSelector";
import {
  trackButtonClick,
  trackVideoView,
  trackFeatureUsage,
} from "./utils/analytics";
import { publishZaloPost } from "./services/zaloService";
import {
  getSocialAccountKey,
  hasZaloAccount,
  loadConnectedSocialAccounts,
  toLegacyFacebookFanpagePayload,
  toSelectedAccountPayload,
} from "./utils/socialChannels";

// S3 Client configuration for video upload
const s3Client = new S3Client({
  region: process.env.REACT_APP_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  },
  requestChecksumCalculation: "WHEN_REQUIRED",
  responseChecksumValidation: "WHEN_REQUIRED",
});

// Video configuration constants
const VIDEO_CONFIG = {
  MAX_SIZE_MB: 150, // Đồng bộ với giới hạn AI
  MAX_DURATION_MINUTES: 15,
  SUPPORTED_TYPES: ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"],
};

const PhotosPostGenerator = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Update initial states to use location state if available
  const [images, setImages] = useState(location.state?.images || []);
  const [productInfo, setProductInfo] = useState(
    location.state?.productInfo || ""
  );
  const [imageUrls, setImageUrls] = useState("");
  const [generatedContent, setGeneratedContent] = useState([]);
  const [selectedPage, setSelectedPage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fanpages, setFanpages] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [selectedFanpages, setSelectedFanpages] = useState([]);
  const [postLength, setPostLength] = useState(DEFAULT_POST_LENGTH);
  const [postStyles, setPostStyles] = useState(DEFAULT_POST_STYLES);
  const [imageUrl, setImageUrl] = useState("");
  const dropdownRef = useRef(null);
  const [userPoint, setUserPoint] = useState(0);
  const [includeContactInfo, setIncludeContactInfo] = useState(false);
  const [settings, setSettings] = useState({
    postLength: DEFAULT_POST_LENGTH,
    postStyles: DEFAULT_POST_STYLES,
    includeContactInfo: false,
  });
  const [selectedLanguage, setSelectedLanguage] = useState("vi"); // Mặc định là tiếng Việt
  const [postCount, setPostCount] = useState(0);
  const [postedCount, setPostedCount] = useState(0);
  const [postStatsService, setPostStatsService] = useState(null);
  const [showTips, setShowTips] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPostText, setEditedPostText] = useState("");
  const [hasEdited, setHasEdited] = useState(false);
  const [isRegeneratingPost, setIsRegeneratingPost] = useState(false);

  // Schedule post states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  // ========== VIDEO SUPPORT STATES ==========
  const [videos, setVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const videoInputRef = useRef(null);
  const [userMembership, setUserMembership] = useState(null);
  const uploadedGenerationImageObjectUrlsRef = useRef(new WeakMap());
  const uploadedGenerationImageStringUrlsRef = useRef(new Map());

  // Platform selection for content generation
  const [contentPlatform, setContentPlatform] = useState("facebook");
  const [zaloArticle, setZaloArticle] = useState(null);

  // Filter accounts by selected content platform
  const filteredFanpages = fanpages.filter((fp) => {
    const fpPlatform = fp.platform || "facebook";
    return fpPlatform === contentPlatform;
  });

  // Lấy thông tin membership
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const db = getDatabase();
      const membershipRef = ref(db, `/users/${userId}/membership`);
      const unsubscribe = onValue(membershipRef, (snapshot) => {
        if (snapshot.exists()) {
          setUserMembership(snapshot.val());
        }
      });
      return () => unsubscribe();
    }
  }, []);
  // ==========================================

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const newImages = [];
      const newVideos = [];

      for (const file of acceptedFiles) {
        if (file.type.startsWith("image/")) {
          newImages.push(file);
        } else if (file.type.startsWith("video/")) {
          // Chặn video nếu chưa có gói Chuyên nghiệp
          if (userMembership < 2) {
            toast.error("Tải video lên chỉ dành cho gói Chuyên nghiệp trở lên. Vui lòng nâng cấp gói dịch vụ!", {
              position: "top-right",
              autoClose: 5000,
            });
            continue;
          }
          newVideos.push(file);
        }
      }

      // CHECK: Mixed types in current drop
      if (newImages.length > 0 && newVideos.length > 0) {
        toast.error("Vui lòng chỉ chọn ảnh hoặc video, không chọn cả hai cùng lúc!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // CHECK: Adding images when videos exist
      if (newImages.length > 0 && videos.length > 0) {
        toast.error("Không thể thêm ảnh khi đã có video. Vui lòng xóa video trước!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // CHECK: Adding videos when images exist
      if (newVideos.length > 0 && images.length > 0) {
        toast.error("Không thể thêm video khi đã có ảnh. Vui lòng xóa ảnh trước!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // CHECK: Video limit (Max 1) - Nếu đã có video, cho phép ghi đè (chỉ lấy video đầu tiên)
      if (newVideos.length > 1) {
        toast.info("Chỉ được tải lên 1 video, đang sử dụng video đầu tiên!", {
          position: "top-right",
          autoClose: 3000,
        });
        // Chỉ giữ lại video đầu tiên
        newVideos.splice(1);
      }

      // Handle Image Limit
      if (images.length + newImages.length > 9) {
        toast.error("Chỉ được phép tải lên tối đa 9 ảnh!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Process Images
      if (newImages.length > 0) {
        const processedImages = await Promise.all(
          newImages.map(async (file) => {
            const processedBlob = await processImage(file);
            return {
              file: processedBlob,
              preview: URL.createObjectURL(processedBlob),
            };
          })
        );
        setImages((prev) => [...prev, ...processedImages]);
      }

      // Process Videos (Chỉ xử lý 1 video, ghi đè nếu đã có)
      if (newVideos.length > 0) {
        setIsVideoUploading(true);
        setVideoUploadProgress(0);
        try {
          const file = newVideos[0]; // Chỉ lấy video đầu tiên
          
          // Validate video
          const videoInfo = await validateVideoFile(file);
          
          // Upload to S3
          const videoId = `vid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const fileName = `videos/${videoId}_${file.name}`;
    
          const parallelUploads3 = new Upload({
            client: s3Client,
            params: {
              Bucket: process.env.REACT_APP_S3_BUCKET_NAME,
              Key: fileName,
              Body: file,
              ContentType: file.type,
            },
          });

          // Track upload progress
          parallelUploads3.on("httpUploadProgress", (progress) => {
            const percent = Math.round((progress.loaded / progress.total) * 100);
            setVideoUploadProgress(percent);
          });
    
          const result = await parallelUploads3.done();
          const videoS3Url = result.Location || `https://${process.env.REACT_APP_S3_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${fileName}`;
    
          // Ghi đè video cũ (nếu có) bằng cách set mảng mới chỉ với 1 video
          setVideos([{
            id: videoId,
            url: videoS3Url,
            file: file,
            preview: URL.createObjectURL(file),
            duration: videoInfo.duration,
            type: "upload",
          }]);

          const isReplacing = videos.length > 0;
          toast.success(isReplacing ? "Đã thay thế video thành công!" : "Đã upload video thành công!", {
            position: "top-right",
            autoClose: 2000,
          });
        } catch (error) {
          console.error("Lỗi khi upload video:", error);
          toast.error(error.message || "Có lỗi xảy ra khi upload video!", {
            position: "top-right",
            autoClose: 3000,
          });
        } finally {
          setIsVideoUploading(false);
          setVideoUploadProgress(0);
        }
      }
    },
    [images, videos]
  );

  const dropzoneAccept = userMembership >= 2
    ? {
        "image/*": [],
        "video/mp4": [],
        "video/webm": [],
        "video/quicktime": [],
        "video/x-msvideo": [],
      }
    : { "image/*": [] };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: dropzoneAccept,
    maxSize: 157286400, // 150MB - đồng bộ với VIDEO_CONFIG.MAX_SIZE_MB
  });

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setGeneratedContent((prev) => prev.filter((_, i) => i !== index));
  };

  // ========== VIDEO HANDLERS ==========
  
  // Validate video file with iOS Safari compatibility
  const validateVideoFile = (file) => {
    return new Promise((resolve, reject) => {
      // Check file size
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > VIDEO_CONFIG.MAX_SIZE_MB) {
        reject(new Error(`Video không được vượt quá ${VIDEO_CONFIG.MAX_SIZE_MB}MB! (Hiện tại: ${sizeMB.toFixed(1)}MB)`));
        return;
      }

      // Check file type - hỗ trợ iOS video/quicktime
      if (!VIDEO_CONFIG.SUPPORTED_TYPES.includes(file.type)) {
        reject(new Error(`Định dạng video không được hỗ trợ! Hỗ trợ: MP4, WebM, MOV, AVI`));
        return;
      }

      // Check duration with iOS Safari compatibility
      const video = document.createElement("video");
      video.preload = "metadata";
      video.playsInline = true; // Required for iOS
      video.muted = true; // Required for autoplay on mobile
      
      let resolved = false;
      
      const handleMetadata = () => {
        if (resolved) return;
        resolved = true;
        
        URL.revokeObjectURL(video.src);
        clearTimeout(timeoutId);
        
        const durationMinutes = video.duration / 60;
        
        if (durationMinutes > VIDEO_CONFIG.MAX_DURATION_MINUTES) {
          reject(new Error(`Video không được dài hơn ${VIDEO_CONFIG.MAX_DURATION_MINUTES} phút! (Hiện tại: ${durationMinutes.toFixed(1)} phút)`));
          return;
        }
        
        resolve({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        });
      };
      
      // iOS Safari sometimes fires canplaythrough before loadedmetadata
      video.onloadedmetadata = handleMetadata;
      video.oncanplaythrough = handleMetadata;
      
      video.onerror = () => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutId);
        URL.revokeObjectURL(video.src);
        reject(new Error("Không thể đọc thông tin video!"));
      };
      
      // Timeout fallback for iOS - resolve với duration ước tính từ file size
      const timeoutId = setTimeout(() => {
        if (resolved) return;
        resolved = true;
        URL.revokeObjectURL(video.src);
        console.warn("Video metadata timeout - using estimated duration for iOS");
        // Ước tính duration: ~2MB/giây cho video chất lượng tốt
        const estimatedDuration = (file.size / (2 * 1024 * 1024)) * 60; // giây
        resolve({
          duration: Math.min(estimatedDuration, VIDEO_CONFIG.MAX_DURATION_MINUTES * 60),
          width: 0,
          height: 0,
        });
      }, 10000); // 10 giây timeout
      
      video.src = URL.createObjectURL(file);
      // Trigger load for iOS
      video.load();
    });
  };

  // Handle video file upload (Cho phép ghi đè video cũ)
  const handleVideoUpload = async (event) => {
    // Check constraints - chỉ chặn khi có ảnh
    if (images.length > 0) {
      toast.error("Không thể thêm video khi đã có ảnh. Vui lòng xóa ảnh trước!");
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const isReplacing = videos.length > 0;
    setIsVideoUploading(true);
    setVideoUploadProgress(0);

    try {
      // Validate video
      const videoInfo = await validateVideoFile(file);
      console.log("Video info:", videoInfo);

      // Upload to S3
      const videoId = `vid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const fileName = `videos/${videoId}_${file.name}`;

      const parallelUploads3 = new Upload({
        client: s3Client,
        params: {
          Bucket: process.env.REACT_APP_S3_BUCKET_NAME,
          Key: fileName,
          Body: file,
          ContentType: file.type,
        },
      });

      parallelUploads3.on("httpUploadProgress", (progress) => {
        const percent = Math.round((progress.loaded / progress.total) * 100);
        setVideoUploadProgress(percent);
        console.log(`Upload progress: ${percent}%`);
      });

      const result = await parallelUploads3.done();
      const videoS3Url = result.Location || `https://${process.env.REACT_APP_S3_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${fileName}`;

      // Ghi đè video cũ (set mảng mới chỉ với 1 video)
      setVideos([{
        id: videoId,
        url: videoS3Url,
        file: file,
        preview: URL.createObjectURL(file),
        duration: videoInfo.duration,
        type: "upload",
      }]);

      toast.success(isReplacing ? "Đã thay thế video thành công!" : "Đã upload video thành công!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Lỗi khi upload video:", error);
      toast.error(error.message || "Có lỗi xảy ra khi upload video!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsVideoUploading(false);
      setVideoUploadProgress(0);
      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }
    }
  };

  // Add video from URL - ĐÃ LOẠI BỎ (không hỗ trợ do CORS issues)
  const handleAddVideoUrl = () => {
    toast.error("Không hỗ trợ thêm video từ URL. Vui lòng tải video lên trực tiếp!", {
      position: "top-right",
      autoClose: 4000,
    });
  };

  // Extract YouTube video ID
  const extractYouTubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : "";
  };

  // Remove video
  const removeVideo = (index) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };
  // =====================================

  const fetchUserPoints = async () => {
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      const pointsRef = ref(db, `users/${userId}/points`);
      const snapshot = await get(pointsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.points !== undefined && data.purchasedPoints !== undefined) {
          setUserPoint(data.points + data.purchasedPoints);
        } else {
          console.error("Invalid points value:", data);
          setUserPoint(0);
        }
      } else {
        setUserPoint(0);
      }
    } catch (error) {
      console.error("Error fetching user points:", error);
      setUserPoint(0);
    }
  };

  const checkUserSubscription = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}/check-subscription`
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        toast.error(response.data.message, {
          position: "top-right",
          autoClose: 5000,
        });
        return {
          success: false,
          error: response.data.error,
        };
      }
    } catch (error) {
      console.error("Error checking subscription:", error);

      return {
        success: false,
        error: error.response?.data?.error || "UNKNOWN_ERROR",
      };
    }
  };

  const checkPoints = () => {
    // Determine cost: 15 points for video content, 10 points for images only
    const requiredPoints = videos.length > 0 ? 15 : 10;

    if (userPoint < requiredPoints) {
      toast.error(
        `Bạn không đủ điểm! Cần ${requiredPoints} điểm để tạo bài viết này (${videos.length > 0 ? "Video" : "Ảnh"}). Vui lòng nạp thêm.`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return false;
    }
    return true;
  };

  const updateUserPoints = async () => {
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      const pointsRef = ref(db, `users/${userId}/points`);
      const snapshot = await get(pointsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.points !== undefined && data.purchasedPoints !== undefined) {
          // Cost: 15 if video, 10 if standard
          const pointsToDeduct = videos.length > 0 ? 15 : 10;
          let newTotal = data.points;
          let newPurchasedPoints = data.purchasedPoints;

          // Ưu tiên trừ điểm từ points trước
          if (newTotal >= pointsToDeduct) {
            newTotal -= pointsToDeduct;
          } else {
            // Nếu points không đủ, trừ hết points và trừ thêm từ purchasedPoints
            const remainingDeduction = pointsToDeduct - newTotal;
            newTotal = 0;
            newPurchasedPoints -= remainingDeduction;
          }

          // Cập nhật điểm mới vào database
          await update(pointsRef, {
            points: newTotal >= 0 ? newTotal : 0,
            purchasedPoints: newPurchasedPoints,
          });

          setUserPoint(newTotal >= 0 ? newTotal : 0 + newPurchasedPoints);
        } else if (typeof data.points === "number") {
          // Xử lý trường hợp cũ sử dụng field points
          const pointsToDeduct = videos.length > 0 ? 15 : 10;
          const newTotal = data.points - pointsToDeduct;
          await update(pointsRef, { points: newTotal >= 0 ? newTotal : 0 });
          setUserPoint(newTotal >= 0 ? newTotal : 0);
        }
      }
    } catch (error) {
      console.error("Lỗi khi cập nhật điểm người dùng:", error);
    }
  };

  const processImage = async (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");

        // Tính toán kích thước mới giữ nguyên tỷ lệ
        let width = img.width;
        let height = img.height;
        const maxSize = 800; // Kích thước tối đa cho chiều dài nhất

        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Chuyển sang định dạng JPEG với chất lượng 0.8
        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/jpeg",
          0.8
        );
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const normalizeGeneratedText = (text = "") =>
    String(text || "")
      .replace(/\*\*/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
      .replace(/\n\n\*/g, "\n*");

  const isHttpUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());

  const uploadImageAssetToCloudinary = async (imageSource) => {
    const formData = new FormData();

    if (typeof imageSource === "string") {
      if (imageSource.startsWith("blob:")) {
        const blobResponse = await fetch(imageSource);
        const blob = await blobResponse.blob();
        formData.append("file", blob, `image-${Date.now()}.jpg`);
      } else {
        formData.append("file", imageSource);
      }
    } else {
      formData.append(
        "file",
        imageSource,
        imageSource?.name || `image-${Date.now()}.jpg`
      );
    }

    formData.append("upload_preset", "AutoWork");
    formData.append("api_key", "238276161954221");

    const response = await axios.post(
      "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.secure_url;
  };

  const resolveImageSourceForGeneration = (image) => {
    const directUrlCandidates = [
      image?.url,
      image?.preview,
      typeof image?.file === "string" ? image.file : null,
      typeof image === "string" ? image : null,
    ].filter(Boolean);

    const remoteUrl = directUrlCandidates.find((candidate) =>
      isHttpUrl(candidate)
    );
    if (remoteUrl) {
      return { kind: "remote", value: remoteUrl };
    }

    if (image?.file instanceof Blob || image?.file instanceof File) {
      return { kind: "upload", value: image.file };
    }

    if (image instanceof Blob || image instanceof File) {
      return { kind: "upload", value: image };
    }

    const inlineSource = directUrlCandidates.find(
      (candidate) =>
        typeof candidate === "string" &&
        (candidate.startsWith("data:") || candidate.startsWith("blob:"))
    );
    if (inlineSource) {
      return { kind: "upload", value: inlineSource };
    }

    return null;
  };

  const resolveImageUrlsForBackendGeneration = async () => {
    const resolvedUrls = [];

    for (const image of images) {
      const source = resolveImageSourceForGeneration(image);

      if (!source) {
        throw new Error("INVALID_IMAGE_SOURCE");
      }

      if (source.kind === "remote") {
        resolvedUrls.push(source.value);
        continue;
      }

      let cachedUrl = null;
      if (typeof source.value === "string") {
        cachedUrl = uploadedGenerationImageStringUrlsRef.current.get(
          source.value
        );
      } else {
        cachedUrl = uploadedGenerationImageObjectUrlsRef.current.get(
          source.value
        );
      }

      if (!cachedUrl) {
        cachedUrl = await uploadImageAssetToCloudinary(source.value);

        if (typeof source.value === "string") {
          uploadedGenerationImageStringUrlsRef.current.set(
            source.value,
            cachedUrl
          );
        } else {
          uploadedGenerationImageObjectUrlsRef.current.set(
            source.value,
            cachedUrl
          );
        }
      }

      resolvedUrls.push(cachedUrl);
    }

    if (resolvedUrls.length === 0) {
      throw new Error("NO_IMAGE_URLS_READY");
    }

    return resolvedUrls;
  };

  const buildBackendGenerationOptions = async () => {
    const hasImages = images.length > 0;
    const hasVideos = videos.length > 0;

    if (hasImages && hasVideos) {
      throw new Error("MIXED_MEDIA_NOT_SUPPORTED");
    }

    if (!hasImages && !hasVideos) {
      throw new Error("MISSING_MEDIA");
    }

    const commonOptions = {
      postLength,
      postStyles,
      productInfo,
      selectedLanguage,
      useFanpageInfo: false,
      customInfo: "",
      platform: contentPlatform,
    };

    if (hasImages) {
      const uploadedImageUrls = await resolveImageUrlsForBackendGeneration();
      return {
        ...commonOptions,
        images: uploadedImageUrls,
      };
    }

    const primaryVideoUrl = videos.find((video) => video?.url)?.url;
    if (!primaryVideoUrl) {
      throw new Error("VIDEO_URL_NOT_READY");
    }

    return {
      ...commonOptions,
      videoSource: primaryVideoUrl,
    };
  };

  const finalizeGeneratedPost = async (generatedText, successMessage) => {
    setGeneratedContent([generatedText]);
    setEditedPostText(generatedText);
    setIsEditMode(false);
    setHasEdited(false);

    updateUserPoints();
    await incrementPostCount();

    toast.success(successMessage, {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const generateMediaPostViaBackend = async ({
    successMessage,
    genericErrorMessage,
  }) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        throw new Error("MISSING_USER_ID");
      }

      const options = await buildBackendGenerationOptions();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/content/generate-product-media-post`,
        {
          userId,
          options,
        }
      );

      if (contentPlatform === "zalo" && response?.data?.data?.zaloArticle) {
        const article = response.data.data.zaloArticle;
        setZaloArticle(article);
        const fullText = (article.bodyParagraphs || []).join("\n\n");
        await finalizeGeneratedPost(fullText, successMessage);
      } else {
        const generatedText = normalizeGeneratedText(
          response?.data?.data?.text || ""
        );

        if (!generatedText) {
          throw new Error("EMPTY_GENERATED_CONTENT");
        }

        setZaloArticle(null);
        await finalizeGeneratedPost(generatedText, successMessage);
      }
      return true;
    } catch (error) {
      console.error("Error generating media post via backend:", error);

      const message =
        error.response?.data?.message ||
        (error.message === "INVALID_IMAGE_SOURCE"
          ? "Không thể chuẩn bị ảnh để tạo bài viết."
          : error.message === "VIDEO_URL_NOT_READY"
            ? "Video chưa sẵn sàng để AI xử lý. Vui lòng chờ upload hoàn tất."
            : error.message === "MIXED_MEDIA_NOT_SUPPORTED"
              ? "Hiện chỉ hỗ trợ tạo bài viết từ ảnh hoặc video riêng biệt."
              : error.message === "MISSING_MEDIA"
                ? "Vui lòng chọn ít nhất một ảnh hoặc video."
                : error.message === "MISSING_USER_ID"
                  ? "Không tìm thấy thông tin người dùng. Vui lòng đăng nhập lại."
                  : error.message === "NO_IMAGE_URLS_READY"
                    ? "Không thể chuẩn bị URL ảnh công khai để tạo bài viết."
                    : genericErrorMessage);

      toast.error(message, {
        position: "top-right",
        autoClose: 5000,
      });
      return false;
    }
  };

  const handleGenerate = async () => {
    if (generatedContent.length > 0 && hasEdited) {
      if (
        !window.confirm(
          "Bạn đã chỉnh sửa bài viết. Tạo bài viết mới sẽ xóa các chỉnh sửa của bạn. Bạn có chắc chắn muốn tiếp tục?"
        )
      ) {
        return;
      }
    }

    setIsEditMode(false);
    setHasEdited(false);

    if (!checkPoints()) return;
    setLoading(true);

    try {
      await generateMediaPostViaBackend({
        successMessage: "Đã tạo mới nội dung bài viết thành công!",
        genericErrorMessage: "Có lỗi xảy ra khi tạo bài viết!",
      });
    } finally {
      setLoading(false);
    }
  };

  const savePostLink = async (fanpageId, postId) => {
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      const timestamp = Date.now(); // Lấy timestamp hiện tại
      const postLink = `https://www.facebook.com/${fanpageId}/posts/${postId}`;
      const formattedLink = `${timestamp}|${postLink}`; // Định dạng mới: timestamp|link
      const postType = "photosPostGenerator"; // Loại bài đăng
      const postLinksRef = ref(db, `users/${userId}/posts/${postType}/links`);

      // Lấy danh sách links hiện tại
      const snapshot = await get(postLinksRef);
      const currentLinks = snapshot.exists() ? snapshot.val() : [];

      // Thêm link mới vào đầu mảng
      const updatedLinks = [formattedLink, ...currentLinks];

      // Lưu lại vào database
      await set(postLinksRef, updatedLinks);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
        const token = localStorage.getItem("userAccessToken");
        await axios.post(
          `${apiUrl}/api/users/${userId}/post-link-index`,
          {
            postType,
            rawLink: formattedLink,
          },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
      } catch (indexError) {
        console.warn("Error indexing post link:", indexError.message);
      }

      return true;
    } catch (error) {
      console.error("Error saving post link:", error);
      return false;
    }
  };

  const uploadImagesToPublicUrls = async () => {
    const postImages = [];

    for (const img of images) {
      try {
        if (typeof img === "string" && img.startsWith("http")) {
          postImages.push(img);
          continue;
        }

        if (img?.url && typeof img.url === "string" && img.url.startsWith("http")) {
          postImages.push(img.url);
          continue;
        }

        let fileToUpload = null;
        if (img?.file && typeof img.file !== "string") {
          fileToUpload = img.file;
        } else if (img instanceof Blob || img instanceof File) {
          fileToUpload = img;
        }

        if (fileToUpload) {
          const formData = new FormData();
          formData.append("file", fileToUpload);
          formData.append("upload_preset", "AutoWork");
          formData.append("api_key", "238276161954221");

          const response = await axios.post(
            "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          postImages.push(response.data.secure_url);
        }
      } catch (uploadErr) {
        console.error("Lỗi upload ảnh lên Cloudinary:", uploadErr);
      }
    }

    return postImages;
  };

  // Hàm lên lịch đăng bài - lưu vào Firebase theo dateKey
  const schedulePost = async ({ date, time }) => {
    if (selectedFanpages.length === 0) {
      toast.error("Vui lòng chọn ít nhất 1 fanpage để lên lịch!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (generatedContent.length === 0) {
      toast.error("Vui lòng tạo bài viết trước khi lên lịch!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsScheduling(true);

    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");

      // Upload ảnh lên Cloudinary để có URL thật (blob URL chỉ tồn tại trong browser)
      let postImages = [];
      if (images.length > 0) {
        toast.info("Đang upload ảnh lên Cloudinary...", {
          position: "top-right",
          autoClose: 2000,
        });

        for (const img of images) {
          try {
            if (typeof img === "string" && img.startsWith("http")) {
              // Đã là URL thật
              postImages.push(img);
              continue;
            }

            let fileToUpload = null;
            if (img.file && typeof img.file !== "string") {
              fileToUpload = img.file;
            } else if (img instanceof Blob || img instanceof File) {
              fileToUpload = img;
            }

            if (fileToUpload) {
              const formData = new FormData();
              formData.append("file", fileToUpload);
              formData.append("upload_preset", "AutoWork");
              formData.append("api_key", "238276161954221");

              const response = await axios.post(
                "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
                formData,
                {
                  headers: { "Content-Type": "multipart/form-data" },
                }
              );
              postImages.push(response.data.secure_url);
            }
          } catch (uploadErr) {
            console.error("Lỗi upload ảnh lên Cloudinary:", uploadErr);
          }
        }
      }

      // Chuẩn bị account data
      const selectedAccounts = selectedFanpages.map(toSelectedAccountPayload);
      const selectedHasZalo = hasZaloAccount(selectedFanpages);
      const fanpageData = selectedFanpages
        .filter((fp) => (fp.platform || "facebook") === "facebook")
        .map(toLegacyFacebookFanpagePayload);

      // Lấy nội dung bài viết từ generatedContent
      const postText = generatedContent
        .map((item) => item.content || item)
        .join("\n\n");

      // Chuẩn hóa dữ liệu video để lưu lịch (không lưu File object vào Firebase)
      const scheduledVideos = videos
        .map((video) => {
          if (!video) return null;
          if (typeof video === "string") {
            return { url: video, type: "upload" };
          }
          if (!video.url) return null;

          return {
            ...(video.id ? { id: video.id } : {}),
            url: video.url,
            type: video.type || "upload",
            ...(typeof video.duration === "number"
              ? { duration: video.duration }
              : {}),
          };
        })
        .filter(Boolean);

      if (videos.length > 0 && scheduledVideos.length === 0) {
        throw new Error("Video chưa sẵn sàng để lên lịch. Vui lòng tải lại video.");
      }

      // Zalo OA bắt buộc có ảnh cover — video-only không đủ
      if (selectedHasZalo && postImages.length === 0) {
        toast.error(
          "Zalo OA bắt buộc có ảnh cover. Vui lòng thêm ít nhất 1 ảnh trước khi lên lịch.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        setIsScheduling(false);
        return;
      }

      // Tạo scheduled post data
      const scheduledPost = {
        content: postText,
        images: postImages,
        videos: scheduledVideos,
        selectedFanpages: fanpageData,
        selectedAccounts,
        scheduleTime: time,
        status: "pending",
        createdAt: Date.now(),
        includeContactInfo: includeContactInfo,
        mediaType:
          scheduledVideos.length > 0
            ? postImages.length > 0
              ? "mixed"
              : "video"
            : postImages.length > 0
              ? "image"
              : "text",
        imageSource: "upload",
        allowNoImage: postImages.length === 0 && scheduledVideos.length === 0,
      };

      // Lưu vào Firebase theo structure: /scheduledNormalPosts/{dateKey}/{userId}/{postId}
      const dateKey = date;
      const scheduleRef = ref(db, `scheduledNormalPosts/${dateKey}/${userId}`);
      const newPostRef = push(scheduleRef);
      await update(ref(db), {
        [`scheduledNormalPosts/${dateKey}/${userId}/${newPostRef.key}`]:
          scheduledPost,
        [`userScheduledNormalPosts/${userId}/${dateKey}/${newPostRef.key}`]:
          scheduledPost,
      });

      setShowScheduleModal(false);

      toast.success(
        `Đã lên lịch đăng bài vào ${new Date(date).toLocaleDateString("vi-VN")} lúc ${time}!`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } catch (error) {
      console.error("Lỗi khi lên lịch đăng bài:", error);
      toast.error("Không thể lên lịch đăng bài. Vui lòng thử lại!", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const handlePost = async () => {
    console.log("handlePost được gọi, selectedFanpages:", selectedFanpages);

    if (selectedFanpages.length === 0) {
      toast.error("Vui lòng chọn một fanpage để đăng bài!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const userAccessToken = localStorage.getItem("userAccessToken");
    const userId = localStorage.getItem("userId");
    const fanpage = selectedFanpages[0];
    const isZaloAccount = (fanpage.platform || "facebook") === "zalo";
    // Lấy thông tin fanpage từ localStorage
    const storedFanpages = JSON.parse(localStorage.getItem("fanpages") || "{}");

    if (!userAccessToken && !isZaloAccount) {
      toast.error("Vui lòng đăng nhập Facebook trước!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    setIsPublishing(true);

    if (isZaloAccount) {
      try {
        const publicImages = await uploadImagesToPublicUrls();

        // Zalo OA bắt buộc có ảnh cover — video-only không đủ
        if (publicImages.length === 0) {
          toast.error(
            "Zalo OA bắt buộc có ảnh cover. Vui lòng thêm ít nhất 1 ảnh trước khi đăng.",
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
          setLoading(false);
          setIsPublishing(false);
          return;
        }
        const videoUrls = videos.map((video) => video?.url || video).filter(Boolean);
        const postText = generatedContent
          .map((item) => item.content || item)
          .join("\n\n");

        const result = await publishZaloPost({
          userId,
          account: fanpage,
          content: postText,
          images: publicImages,
          videos: videoUrls,
          generatorKey: "photosPostGenerator",
          zaloArticle: zaloArticle || undefined,
        });

        if (!result?.success) {
          throw new Error(
            result?.result?.errorMessage ||
              result?.message ||
              "Zalo không trả về trạng thái thành công"
          );
        }

        toast.success(`Đăng bài thành công lên ${fanpage.name}!`, {
          position: "top-right",
          autoClose: 3000,
        });
        await incrementPostedCount();
      } catch (error) {
        console.error(`Failed to post to ${fanpage.name}:`, error);
        toast.error(`Không thể đăng bài lên ${fanpage.name}! ${error.message || ""}`, {
          position: "top-right",
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
        setIsPublishing(false);
      }
      return;
    }

    // Kiểm tra xem fanpage có access_token không (dữ liệu từ Firebase có thể đã có)
    if (!fanpage.access_token) {
      try {
        // Thử lấy access_token từ Facebook API
        const response = await axios.get(
          `https://graph.facebook.com/v21.0/me/accounts`,
          {
            params: { access_token: userAccessToken },
            timeout: 15000,
          }
        );

        if (!response.data || response.data.error) {
          throw response.data?.error || new Error("Không thể lấy access token cho fanpage");
        }

        // Tìm fanpage tương ứng từ response (axios.data = Graph API response, .data = mảng pages)
        const fbFanpage = response.data.data.find((page) => page.id === fanpage.id);

        if (fbFanpage && fbFanpage.access_token) {
          // Cập nhật access_token cho fanpage hiện tại
          fanpage.access_token = fbFanpage.access_token;

          // Lưu access_token vào Firebase để dùng sau này
          try {
            const db = getDatabase();
            const userId = localStorage.getItem("userId");
            const fanpageRef = ref(
              db,
              `users/${userId}/connectedFanpages/${fanpage.id}`
            );
            await update(fanpageRef, { access_token: fbFanpage.access_token });
          } catch (updateError) {
            console.error(
              "Không thể cập nhật access_token vào Firebase:",
              updateError
            );
          }
        } else {
          throw new Error("Không tìm thấy access token cho fanpage này");
        }
      } catch (error) {
        console.error("Lỗi khi lấy access token cho fanpage:", error);
        toast.error(
          "Không thể lấy quyền truy cập cho fanpage. Vui lòng kết nối lại fanpage trong trang Thiết lập thông tin!",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        setLoading(false);
        setIsPublishing(false);
        return;
      }
    }

    const uploadImage = (image, fanpage) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("access_token", fanpage.access_token);
        formData.append("source", image.file);
        formData.append("published", false);

        axios
          .post(`https://graph.facebook.com/${fanpage.id}/photos`, formData)
          .then((response) => {
            resolve(response.data.id);
          })
          .catch((error) => {
            reject(error);
          });
      });
    };

    try {
      // Chỉ đăng bài lên 1 fanpage đã chọn
      try {
        // Upload ảnh nếu có
        let attachedMedia = [];
        if (images.length > 0) {
          const photoIds = await Promise.all(
            images.map((image) => uploadImage(image, fanpage))
          );
          attachedMedia = photoIds.map((id) => ({ media_fbid: id }));
        }

        // Lấy thông tin fanpage từ Firebase
        let fanpageInfo = storedFanpages[fanpage.id] || {};

        // Nếu không có thông tin từ localStorage, thử lấy từ Firebase
        if (!fanpageInfo.fanpageInfo) {
          try {
            const db = getDatabase();
            const userId = localStorage.getItem("userId");
            const fanpageInfoRef = ref(
              db,
              `users/${userId}/fanpages/${fanpage.id}`
            );
            const snapshot = await get(fanpageInfoRef);

            if (snapshot.exists()) {
              fanpageInfo = snapshot.val();
            }
          } catch (error) {
            console.error(
              "Không thể lấy thông tin fanpage từ Firebase:",
              error
            );
          }
        }

        let footerText = "";

        // Chỉ thêm footer text khi includeContactInfo được bật
        if (includeContactInfo && fanpageInfo.fanpageInfo) {
          footerText = `

${fanpageInfo.fanpageInfo || ""}`;
        }

        // Kết hợp nội dung bài viết với footer
        const fullMessage = generatedContent.join("\n\n") + footerText;

        // Xử lý đăng bài dựa trên loại media
        if (videos.length > 0 && images.length === 0) {
          // CHỈ có video - đăng video lên fanpage
          const video = videos[0]; // Đăng video đầu tiên
          
          // Sử dụng axios thay vì FB.api để tránh hang khi không có SDK session
          console.log("📤 Gọi Graph API để đăng video lên fanpage:", fanpage.id);
          const videoResponse = await axios.post(
            `https://graph.facebook.com/v21.0/${fanpage.id}/videos`,
            {
              file_url: video.url,
              description: fullMessage,
              access_token: fanpage.access_token,
            },
            { timeout: 60000 } // 60s timeout cho video
          );

          if (videoResponse.data && videoResponse.data.id) {
            console.log("✅ Đăng video thành công, videoId:", videoResponse.data.id);
            await savePostLink(fanpage.id, videoResponse.data.id);
          } else {
            throw new Error("Facebook không trả về video ID");
          }
        } else if (images.length > 0) {
          // Có ảnh (có thể kèm video link trong message)
          let messageWithVideo = fullMessage;
          if (videos.length > 0) {
            // Thêm link video vào cuối message nếu có
            const videoLinks = videos.map((v, i) => 
              `\n🎬 Video ${i + 1}: ${v.url}`
            ).join("");
            messageWithVideo += videoLinks;
          }

          // Sử dụng axios thay vì FB.api để tránh hang khi không có SDK session
          console.log("📤 Gọi Graph API để đăng bài (ảnh) lên fanpage:", fanpage.id);
          const postResponse = await axios.post(
            `https://graph.facebook.com/v21.0/${fanpage.id}/feed`,
            {
              message: messageWithVideo,
              access_token: fanpage.access_token,
              attached_media: attachedMedia,
            },
            { timeout: 30000 } // 30s timeout
          );

          if (postResponse.data && postResponse.data.id) {
            console.log("✅ Đăng bài thành công, postId:", postResponse.data.id);
            await savePostLink(fanpage.id, postResponse.data.id);
          } else {
            throw new Error("Facebook không trả về post ID");
          }
        }

        toast.success(`Đăng bài thành công lên ${fanpage.name}!`, {
          position: "top-right",
          autoClose: 3000,
        });
        await incrementPostedCount();
      } catch (error) {
        console.error(`Failed to post to ${fanpage.name}:`, error);
        toast.error(
          `Không thể đăng bài lên ${fanpage.name}! ${error.message || ""}`,
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Failed to publish posts:", error);
    } finally {
      setLoading(false);
      setIsPublishing(false);
    }
  };

  const loadImagesFromUrls = async () => {
    const urls = imageUrls.split("\n").filter((url) => url.trim());
    if (urls.length === 0) {
      toast.error("Vui lòng nhập ít nhất một URL ảnh hợp lệ!");
      return;
    }

    try {
      const newImages = await Promise.all(
        urls.map(async (url) => {
          const response = await fetch(url);
          const blob = await response.blob();
          const file = new File([blob], `image-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          return {
            file,
            preview: url,
          };
        })
      );
      setImages((prev) => [...prev, ...newImages]);
      setImageUrls("");
    } catch (error) {
      toast.error("Có lỗi khi tải ảnh từ URL. Vui lòng kiểm tra lại các URL!");
    }
  };

  const handleAddImageUrl = async () => {
    if (videos.length > 0) {
      toast.error("Không thể thêm ảnh khi đang có video. Vui lòng xóa video trước!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!imageUrl.trim()) {
      toast.error("Vui lòng nhập URL ảnh!");
      return;
    }

    // Kiểm tra số lượng ảnh trước khi thêm
    if (images.length >= 9) {
      toast.error("Chỉ được phép tải lên tối đa 6 ảnh!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], `image-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      setImages((prev) => [
        ...prev,
        {
          file,
          preview: imageUrl,
        },
      ]);
      setImageUrl("");
    } catch (error) {
      toast.error("URL ảnh không hợp lệ. Vui lòng kiểm tra lại!");
    }
  };

  useEffect(() => {
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    // If we have images from location state, no need to load them again
    if (location.state?.images) {
      setImages(location.state.images);
    }

    fetchUserPoints();
    fetchPostCount();
    fetchPostedCount();

    // Lấy danh sách fanpage từ Firebase (Firebase-first pattern)
    const loadFanpagesFromFirebase = async () => {
      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");
        const fanpagesFromFirebase = await loadConnectedSocialAccounts(db, userId);

        if (fanpagesFromFirebase.length > 0) {
          console.log("Accounts được tải từ Firebase:", fanpagesFromFirebase);
          setFanpages(fanpagesFromFirebase);

          if (fanpagesFromFirebase.length === 1) {
            setSelectedPage(fanpagesFromFirebase[0]);
            setSelectedFanpages([fanpagesFromFirebase[0]]);
          } else {
            setSelectedPage("");
            setSelectedFanpages([]);
          }
        } else {
          console.error("Không tìm thấy account Facebook/Zalo trong Firebase");
        }
      } catch (error) {
        console.error("Lỗi khi tải accounts từ Firebase:", error);
        toast.error(
          "Có lỗi khi tải dữ liệu tài khoản đăng bài. Vui lòng thử lại sau!",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    };

    loadFanpagesFromFirebase();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFanpageSelect = (fanpage) => {
    // Đóng dropdown sau khi chọn fanpage
    setIsDropdownOpen(false);

    // Chỉ chọn một fanpage duy nhất
    setSelectedFanpages([fanpage]);
    console.log("Đã chọn account:", fanpage.name);

    // Lưu thông tin fanpage đã chọn vào localStorage để dễ truy cập
    try {
      if (fanpage.platform === "zalo") {
        return;
      }

      const db = getDatabase();
      const userId = localStorage.getItem("userId");

      // Lấy thông tin fanpage từ Firebase nếu có
      const fanpageInfoRef = ref(db, `users/${userId}/fanpages/${fanpage.id}`);
      get(fanpageInfoRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const fanpageInfo = snapshot.val();
            // Lưu vào localStorage để tiện sử dụng
            const storedFanpages = JSON.parse(
              localStorage.getItem("fanpages") || "{}"
            );
            storedFanpages[fanpage.id] = { ...fanpageInfo };
            localStorage.setItem("fanpages", JSON.stringify(storedFanpages));
          }
        })
        .catch((error) => {
          console.error("Lỗi khi đọc thông tin fanpage từ Firebase:", error);
        });
    } catch (error) {
      console.error("Lỗi khi lưu thông tin fanpage đã chọn:", error);
    }
  };

  const handlePostStyleChange = (style) => {
    const newStyles = postStyles.includes(style)
      ? postStyles.filter((s) => s !== style)
      : [...postStyles, style];
    setPostStyles(newStyles);
    const newSettings = {
      ...settings,
      postStyles: newStyles,
    };
    saveUserSettings(newSettings);
  };

  const handleIncludeContactInfoChange = (e) => {
    const newValue = !includeContactInfo;
    setIncludeContactInfo(newValue);
    const newSettings = {
      ...settings,
      includeContactInfo: newValue,
    };
    saveUserSettings(newSettings);
  };

  // Hàm xử lý thay đổi ngôn ngữ
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    // Lưu vào localStorage
    localStorage.setItem("selectedLanguage", language);
    // Lưu vào settings để sync với Firebase
    const newSettings = {
      ...settings,
      selectedLanguage: language,
    };
    saveUserSettings(newSettings);
  };

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const settingsRef = ref(
          db,
          `users/${userId}/settings/photosPostGenerator`
        );
        const snapshot = await get(settingsRef);

        if (snapshot.exists()) {
          const settings = snapshot.val();
          setPostLength(settings.postLength || DEFAULT_POST_LENGTH);
          setPostStyles(settings.postStyles || DEFAULT_POST_STYLES);
          setIncludeContactInfo(settings.includeContactInfo || false);
          setSelectedLanguage(settings.selectedLanguage || "vi");
        } else {
          // Nếu không có settings trên Firebase, thử load từ localStorage
          const savedLanguage =
            localStorage.getItem("selectedLanguage") || "vi";
          setSelectedLanguage(savedLanguage);
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    };

    loadUserSettings();
  }, []);

  const saveUserSettings = async (newSettings = null) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const settingsRef = ref(
        db,
        `users/${userId}/settings/photosPostGenerator`
      );

      const settingsToSave = newSettings || {
        ...settings,
        selectedLanguage: selectedLanguage,
      };
      await set(settingsRef, settingsToSave);
      setSettings(settingsToSave);
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("userId");
  }, []);

  const fetchPostCount = async () => {
    if (!postStatsService) return;
    const count = await postStatsService.getGeneratedPostCount();
    setPostCount(count);
  };

  const fetchPostedCount = async () => {
    if (!postStatsService) return;
    const count = await postStatsService.getPostedCount();
    setPostedCount(count);
  };

  const incrementPostCount = async () => {
    if (!postStatsService) return;
    try {
      const newCount = await postStatsService.incrementGeneratedPostCount(
        postCount
      );
      setPostCount(newCount);
    } catch (error) {
      console.error("Error incrementing post count:", error);
    }
  };

  const incrementPostedCount = async () => {
    if (!postStatsService) return;
    try {
      const newCount = await postStatsService.incrementPostedCount(postedCount);
      setPostedCount(newCount);
    } catch (error) {
      console.error("Error incrementing posted count:", error);
    }
  };

  const handleWatchTutorial = () => {
    trackVideoView("photos_post_tutorial");
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      if (editedPostText !== generatedContent[0]) {
        setGeneratedContent([editedPostText]);
        setHasEdited(true);
        toast.success("Đã lưu thay đổi nội dung bài viết!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.info("Không có thay đổi để lưu.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } else {
      setEditedPostText(generatedContent[0]);
    }
    setIsEditMode(!isEditMode);
  };

  const cancelEdit = () => {
    setEditedPostText(generatedContent[0]);
    setIsEditMode(false);
  };

  const copyContent = (text, label) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`Đã sao chép ${label} vào clipboard!`, {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .catch((error) => {
        console.error("Lỗi khi sao chép:", error);
        toast.error("Không thể sao chép. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  const regeneratePost = async () => {
    setIsRegeneratingPost(true); // Set trạng thái loading khi bắt đầu

    if (!checkPoints()) {
      setIsRegeneratingPost(false);
      return;
    }

    try {
      await generateMediaPostViaBackend({
        successMessage: "Đã viết lại nội dung bài viết thành công!",
        genericErrorMessage: "Có lỗi xảy ra khi tạo lại bài viết!",
      });
    } finally {
      setIsRegeneratingPost(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          TẠO BÀI VIẾT TỪ ẢNH & VIDEO VỚI AI
        </h1>

        <PointWarning
          title="Vui lòng nạp thêm điểm để tiếp tục dùng chức năng này nhé ;)"
          userPoint={userPoint}
        />

        <div className="flex flex-col md:flex-row gap-6">
          {/* Phần bên trái - Form nhập thông tin */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-md shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Tải lên ảnh
                </h2>
                <a
                  href="https://youtu.be/Jdfb0ytaEEU"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleWatchTutorial}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md shadow-sm transition duration-300 hover:text-red-600"
                  title="Xem video hướng dẫn cách sử dụng"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                  <span>Hướng dẫn</span>
                </a>
              </div>

              {/* Platform selection tabs */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Nền tảng nội dung
                </label>
                <div className="flex p-1 space-x-1 bg-gray-100 rounded-lg shadow-inner">
                  <button
                    type="button"
                    className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 flex items-center justify-center ${
                      contentPlatform === "facebook"
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setContentPlatform("facebook");
                      setZaloArticle(null);
                      setSelectedFanpages([]);
                    }}
                  >
                    <FiFacebook className="mr-2" />
                    Facebook
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 flex items-center justify-center ${
                      contentPlatform === "zalo"
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setContentPlatform("zalo");
                      setSelectedFanpages([]);
                    }}
                  >
                    <span className="mr-2 font-bold">Z</span>
                    Zalo OA
                  </button>
                </div>
                {contentPlatform === "zalo" && (
                  <p className="mt-2 text-sm text-blue-600">
                    Bài viết sẽ được tạo theo định dạng Zalo OA Article (tiêu đề, mô tả, nội dung riêng biệt)
                  </p>
                )}
              </div>

              {/* Khu vực kéo thả ảnh */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block mr-2 mb-1"
                  >
                    <path
                      d="M4 16L8.586 11.414C8.96106 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.0389 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.9611 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.0389 12.0391 18.414 12.414L20 14M14 8H14.01M6 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V6C20 5.46957 19.7893 4.96086 19.4142 4.58579C19.0391 4.21071 18.5304 4 18 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{"Tải ảnh & video (Kéo thả hoặc chọn file)"} {userMembership < 2 && <span className="text-xs text-amber-600">(🔒 Video: gói Chuyên nghiệp)</span>}</span>
                </label>

                {/* Khu vực kéo thả ảnh */}
                <div
                  {...getRootProps()}
                  className={`relative border-2 border-dashed rounded-md p-4 transition-colors cursor-pointer
                    ${
                      isDragActive
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/30"
                    }`}
                >
                  <input {...getInputProps()} id="file-input" />

                  {images.length === 0 ? (
                    <div className="py-4 flex flex-col items-center justify-center">
                      <div className="mx-auto w-20 h-20 mb-4 flex items-center justify-center rounded-full bg-blue-50">
                        <FiUpload className="text-3xl text-blue-500" />
                      </div>
                      <p className="text-base text-gray-600 font-medium text-center">
                        Kéo thả ảnh vào đây hoặc
                      </p>
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
                      >
                        Chọn ảnh hoặc video từ máy tính
                      </button>
                      <p className="mt-2 text-sm text-gray-500 text-center">
                        Ảnh: JPG, PNG • Video: MP4, MOV (Max 100MB)
                        {userMembership < 2 && (
                          <span className="block text-amber-600 text-xs mt-1">
                            ⬆️ Nâng cấp gói Chuyên nghiệp để tải video lên
                          </span>
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="py-2">
                      {/* Hiển thị hướng dẫn thêm ảnh */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex-1 flex flex-col justify-center items-center sm:items-start">
                          <p className="font-medium text-gray-700 mb-1 text-center sm:text-left">
                            Thêm ảnh mới
                          </p>
                          <p className="text-xs text-gray-500 text-center sm:text-left">
                            Nhấn nút hoặc kéo thả vào khu vực này
                          </p>
                        </div>
                        <div className="flex flex-row flex-wrap justify-center gap-2">
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                          >
                            <FiUpload className="text-gray-500" />
                            <span>Từ máy tính</span>
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              document
                                .getElementById("image-url-input")
                                .focus();
                            }}
                          >
                            <FiLink className="text-gray-500" />
                            <span>Từ URL</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Hiển thị ảnh đã tải lên */}
              {images.length > 0 && (
                <div className="mb-6">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {images.map((image, index) => (
                      <div
                        key={index}
                        className="group relative aspect-square bg-gray-50 border border-gray-200 rounded-md overflow-hidden"
                      >
                        <img
                          src={image.preview}
                          alt={`Ảnh sản phẩm ${index + 1}`}
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(index);
                            }}
                            className="p-1.5 bg-white/90 text-red-500 rounded-full hover:bg-white"
                          >
                            <MdClose size={18} />
                          </button>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center truncate">
                          Ảnh {index + 1}
                        </div>
                      </div>
                    ))}

                    {images.length < 9 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          document.getElementById("file-input").click();
                        }}
                        className="aspect-square border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30"
                      >
                        <FiPlus className="text-2xl text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1 text-center px-2">
                          Thêm ảnh/video
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Hiển thị Video Upload Progress */}
              {isVideoUploading && (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="animate-spin">
                      <FiVideo className="text-xl text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-purple-800">
                        Đang tải video lên...
                      </p>
                      <p className="text-xs text-purple-600">
                        {videoUploadProgress}% hoàn thành
                      </p>
                    </div>
                    <span className="text-lg font-bold text-purple-700">
                      {videoUploadProgress}%
                    </span>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full bg-purple-200 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${videoUploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-purple-500 mt-2 text-center">
                    Vui lòng chờ trong giây lát, không đóng trang này...
                  </p>
                </div>
              )}

              {/* Hiển thị Video đã tải lên (Gộp vào danh sách hiển thị) */}
              {videos.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Video đã chọn:</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {videos.map((video, index) => (
                      <div
                        key={video.id}
                        className="group relative aspect-video bg-gray-900 rounded-md overflow-hidden border border-gray-200"
                      >
                        <video
                          src={video.preview || video.url}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        
                        {/* Play icon overlay */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-white/80 rounded-full p-2">
                            <FiPlay className="text-xl text-purple-600" />
                          </div>
                        </div>

                        {/* Duration badge */}
                        {video.duration && (
                          <div className="absolute bottom-2 left-2 bg-black/75 text-white text-[10px] px-1.5 py-0.5 rounded">
                            {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, "0")}
                          </div>
                        )}

                        {/* Type badge */}
                        <div className="absolute top-2 left-2 bg-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded shadow-sm">
                          Video
                        </div>

                        {/* Remove button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeVideo(index);
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-white/90 text-red-500 rounded-full hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        >
                          <MdClose size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                  <FiLink className="text-blue-500" />
                  <label
                    className="text-gray-700 font-medium"
                    id="media-url-label"
                  >
                    Thêm ảnh từ URL
                  </label>
                </div>

                <div className="relative">
                  <input
                    id="image-url-input"
                    type="text"
                    className="w-full pl-4 pr-28 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    aria-labelledby="media-url-label"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        if (!imageUrl) return;
                        
                        // Chặn video URL - chỉ hỗ trợ upload video trực tiếp
                        const isVideo = imageUrl.match(/\.(mp4|webm|ogg|mov|avi)$/i) || 
                                       imageUrl.includes("youtube.com") || 
                                       imageUrl.includes("youtu.be");
                        
                        if (isVideo) {
                          toast.error("Không hỗ trợ thêm video từ URL. Vui lòng tải video lên trực tiếp!", {
                            position: "top-right",
                            autoClose: 4000,
                          });
                          return;
                        }
                        
                        // Treat as image
                        handleAddImageUrl();
                      }}
                      disabled={!imageUrl.trim()}
                      className={`h-full px-4 rounded-r-md text-white font-medium transition-colors ${
                        !imageUrl.trim()
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      Thêm
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Hỗ trợ ảnh: JPG, PNG, GIF, WebP. Video chỉ hỗ trợ tải lên trực tiếp.
                </p>
              </div>
              {/* ========================================== */}

              {/* Cải tiến phần nhập thông tin sản phẩm */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label
                    htmlFor="product-info"
                    className="text-gray-700 font-medium"
                  >
                    Thông tin sản phẩm và yêu cầu bài viết
                  </label>
                </div>

                <div className="relative">
                  <textarea
                    id="product-info"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    rows="4"
                    value={productInfo}
                    onChange={(e) => setProductInfo(e.target.value)}
                    placeholder="Mô tả chi tiết về sản phẩm: tên, đặc điểm, công dụng, giá cả, cách dùng,..."
                  />
                  {productInfo.length > 0 && (
                    <div className="absolute right-2 bottom-2 text-xs text-gray-500 bg-white px-1.5 py-0.5 rounded-full border border-gray-200">
                      {productInfo.length} ký tự
                    </div>
                  )}
                </div>

                <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowTips(!showTips)}
                    className="w-full flex items-center justify-between text-sm text-blue-700 font-medium focus:outline-none"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className="h-5 w-5 text-blue-500 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Gợi ý thông tin nên có</span>
                    </div>
                    <FiChevronDown
                      className={`w-5 h-5 text-blue-500 transition-transform duration-200 ${
                        showTips ? "transform rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      showTips
                        ? "mt-2 pl-7 max-h-40 opacity-100"
                        : "max-h-0 opacity-0 mt-0 pl-7"
                    }`}
                  >
                    <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                      <li>Tên sản phẩm, thương hiệu, xuất xứ, nguyên liệu</li>
                      <li>
                        Công dụng, đặc điểm nổi bật, lợi ích cho người dùng
                      </li>
                      <li>Giá bán, chương trình khuyến mãi (nếu có)</li>
                      <li>
                        Các yêu cầu đặc biệt cho bài viết (phong cách, đối
                        tượng,...)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Thêm PostLanguageSelector vào đây */}
              <PostLanguageSelector
                selectedLanguage={selectedLanguage}
                onLanguageChange={handleLanguageChange}
                disabled={loading || isRegeneratingPost}
              />

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Độ dài bài viết
                </label>
                <div className="flex w-full rounded-md shadow-sm">
                  {POST_LENGTHS.map(({ value, label }, index) => (
                    <button
                      key={value}
                      type="button"
                      className={`flex-1 py-2 px-2 text-center text-sm font-medium
                        ${index === 0 ? "rounded-l-md" : ""}
                        ${
                          index === POST_LENGTHS.length - 1
                            ? "rounded-r-md"
                            : ""
                        }
                        ${
                          postLength === value
                            ? "bg-blue-600 text-white z-10"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => setPostLength(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Phong cách bài viết
                </label>
                <div className="flex flex-wrap gap-2">
                  {POST_STYLES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`py-2 px-4 rounded-full text-sm font-medium 
                        ${
                          postStyles.includes(value)
                            ? "bg-blue-600 text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => handlePostStyleChange(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <label className="block text-gray-700 font-medium">
                    Thêm thông tin liên hệ vào cuối bài viết
                  </label>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="contactInfo"
                      id="contactInfo"
                      checked={includeContactInfo}
                      onChange={handleIncludeContactInfoChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="contactInfo"
                      className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                        ${includeContactInfo ? "bg-blue-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                          ${
                            includeContactInfo
                              ? "translate-x-6 bg-white border-2 border-blue-500"
                              : "translate-x-0 bg-white"
                          }`}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Chọn tài khoản để đăng bài
                </label>
                {filteredFanpages.length === 0 ? (
                  <button
                    onClick={() => {
                      navigate("/profile");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "facebook-fanpage-management"
                        );
                        if (element) {
                          element.scrollIntoView({ behavior: "smooth" });
                        }
                      }, 500);
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base"
                  >
                    {contentPlatform === "zalo"
                      ? "Kết nối tài khoản Zalo OA để đăng bài"
                      : "Thiết lập tài khoản Facebook để đăng bài"}
                  </button>
                ) : (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={toggleDropdown}
                      type="button"
                      className="w-full flex justify-between items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                    >
                      <span>
                        {selectedFanpages.length > 0
                          ? selectedFanpages[0].name
                          : "Chọn tài khoản"}
                      </span>
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredFanpages.map((fanpage) => (
                          <div
                            key={getSocialAccountKey(fanpage)}
                            onClick={() => handleFanpageSelect(fanpage)}
                            className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                              selectedFanpages.find(
                                (fp) =>
                                  getSocialAccountKey(fp) ===
                                  getSocialAccountKey(fanpage)
                              )
                                ? "bg-blue-50 text-blue-600"
                                : ""
                            }`}
                          >
                            <span>{fanpage.name}</span>
                            <span className="float-right text-xs text-gray-500 uppercase">
                              {fanpage.platform === "zalo" ? "Zalo OA" : "Facebook"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <button
                  onClick={handleGenerate}
                  disabled={(images.length === 0 && videos.length === 0) || loading || userPoint <= 0}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base mt-4 font-medium"
                >
                  {loading ? (
                    <ClipLoader size={20} color={"#fff"} />
                  ) : (
                    <FiRefreshCw />
                  )}
                  <span>{loading ? "Đang tạo..." : "Tạo bài viết"}</span>
                </button>
                {userPoint <= 0 && (
                  <p className="text-red-500 text-sm mt-2 text-center">
                    Bạn không đủ điểm để tạo bài viết. Vui lòng nạp thêm điểm!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Phần bên phải - Nội dung và đăng bài */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-md shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Nội dung bài viết
                </h2>
                {generatedContent.length > 0 && !isEditMode && (
                  <div className="flex gap-2">
                    <button
                      onClick={toggleEditMode}
                      className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md font-medium text-sm flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                      Sửa
                    </button>
                    <button
                      onClick={regeneratePost}
                      disabled={isRegeneratingPost}
                      className="px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-md font-medium text-sm flex items-center"
                    >
                      <FiRefreshCw
                        className={`h-4 w-4 mr-1 ${
                          isRegeneratingPost ? "animate-spin" : ""
                        }`}
                      />
                      {isRegeneratingPost ? "Đang viết lại..." : "Viết lại"}
                    </button>
                  </div>
                )}
              </div>

              {generatedContent.length > 0 ? (
                isEditMode ? (
                  <div className="mb-4">
                    <div className="relative">
                      <textarea
                        className="w-full p-4 bg-white rounded-md text-base border border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 whitespace-pre-line break-words resize-none overflow-hidden"
                        value={editedPostText}
                        onChange={(e) => {
                          setEditedPostText(e.target.value);
                          // Tự động điều chỉnh chiều cao
                          e.target.style.height = "auto";
                          e.target.style.height =
                            Math.max(100, e.target.scrollHeight) + "px";
                        }}
                        onKeyDown={(e) => {
                          if (e.ctrlKey && e.key === "Enter") {
                            toggleEditMode();
                          }
                          if (e.key === "Escape") {
                            cancelEdit();
                          }
                        }}
                        ref={(el) => {
                          if (el) {
                            el.style.height = "auto";
                            el.style.height =
                              Math.max(100, el.scrollHeight) + "px";
                          }
                        }}
                        placeholder="Nhập nội dung bài viết..."
                      ></textarea>
                      <div className="absolute bottom-2 right-3 text-xs text-gray-500 bg-white px-1.5 py-0.5 rounded-full border border-gray-200">
                        {editedPostText.length} ký tự
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="text-xs text-gray-500 hidden sm:block">
                        <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">
                          Ctrl
                        </kbd>{" "}
                        +{" "}
                        <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">
                          Enter
                        </kbd>{" "}
                        để lưu •{" "}
                        <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">
                          Esc
                        </kbd>{" "}
                        để hủy
                      </div>
                      <div className="flex gap-2 ml-auto">
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm"
                        >
                          Hủy
                        </button>
                        <button
                          onClick={toggleEditMode}
                          className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium text-sm"
                        >
                          Lưu thay đổi
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                  {/* Chỉ hiển thị plain text preview khi KHÔNG phải Zalo hoặc chưa có zaloArticle */}
                  {!(contentPlatform === "zalo" && zaloArticle) && (
                  <div className="relative mb-4">
                    <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                      <div
                        className="whitespace-pre-line"
                        style={{ minHeight: "100px", height: "auto" }}
                      >
                        {generatedContent[0]}
                      </div>
                    </div>
                    <button
                      onClick={() => copyContent(generatedContent[0], "nội dung")}
                      className="absolute top-2 right-2 p-2 text-gray-400 hover:text-blue-500 bg-white hover:bg-blue-50 rounded-md transition-colors border border-gray-200 shadow-sm"
                      title="Sao chép nội dung"
                    >
                      <FiCopy className="w-4 h-4" />
                    </button>
                  </div>
                  )}

                  {/* Zalo Article Preview */}
                  {contentPlatform === "zalo" && zaloArticle && !isEditMode && (
                    <div className="mb-4 border border-blue-200 rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-200">
                        <h3 className="text-sm font-semibold text-blue-800 flex items-center">
                          <span className="mr-2 font-bold text-blue-600">Z</span>
                          Cấu trúc Zalo OA Article
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {/* Title */}
                        <div className="relative">
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                              Tiêu đề
                            </label>
                            <span className={`text-xs ${(zaloArticle.title || "").length > 150 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                              {(zaloArticle.title || "").length}/150
                            </span>
                          </div>
                          <div className="relative">
                            <textarea
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-base font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                              rows="2"
                              value={zaloArticle.title || ""}
                              onChange={(e) =>
                                setZaloArticle((prev) => ({
                                  ...prev,
                                  title: e.target.value,
                                }))
                              }
                            />
                            <button
                              onClick={() => copyContent(zaloArticle.title, "tiêu đề")}
                              className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-blue-500 bg-white hover:bg-blue-50 rounded-md transition-colors border border-gray-100 shadow-sm"
                              title="Sao chép tiêu đề"
                            >
                              <FiCopy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="relative">
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                              Mô tả
                            </label>
                            <span className={`text-xs ${(zaloArticle.description || "").length > 300 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                              {(zaloArticle.description || "").length}/300
                            </span>
                          </div>
                          <div className="relative">
                            <textarea
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows="3"
                              value={zaloArticle.description || ""}
                              onChange={(e) =>
                                setZaloArticle((prev) => ({
                                  ...prev,
                                  description: e.target.value,
                                }))
                              }
                            />
                            <button
                              onClick={() => copyContent(zaloArticle.description, "mô tả")}
                              className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-blue-500 bg-white hover:bg-blue-50 rounded-md transition-colors border border-gray-100 shadow-sm"
                              title="Sao chép mô tả"
                            >
                              <FiCopy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Body - Single textarea for all content */}
                        <div className="relative">
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                              Nội dung bài viết
                            </label>
                            <span className="text-xs text-gray-400">
                              {(zaloArticle.bodyParagraphs || []).join("\n\n").length} ký tự
                            </span>
                          </div>
                          <div className="relative">
                            <textarea
                              className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 leading-relaxed"
                              rows={Math.max(6, (zaloArticle.bodyParagraphs || []).length * 3)}
                              value={(zaloArticle.bodyParagraphs || []).join("\n\n")}
                              onChange={(e) => {
                                const paragraphs = e.target.value.split(/\n\n+/).filter(Boolean);
                                setZaloArticle((prev) => ({
                                  ...prev,
                                  bodyParagraphs: paragraphs.length > 0 ? paragraphs : [""],
                                }));
                                setEditedPostText(e.target.value);
                                setHasEdited(true);
                              }}
                            />
                            <button
                              onClick={() => copyContent((zaloArticle.bodyParagraphs || []).join("\n\n"), "nội dung")}
                              className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-blue-500 bg-white hover:bg-blue-50 rounded-md transition-colors border border-gray-100 shadow-sm"
                              title="Sao chép nội dung"
                            >
                              <FiCopy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  </>
                )
              ) : (
                <div className="mb-4 p-4 bg-white rounded-md border border-gray-200 min-h-[200px] flex flex-col items-center justify-center text-gray-400">
                  <svg
                    className="w-16 h-16 mb-2 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                  </svg>
                  <span className="text-center">Chưa có bài viết nào</span>
                  <span className="text-center text-sm mt-1">
                    Nhập yêu cầu bài viết và nhấn "Tạo bài viết" để bắt đầu
                  </span>
                </div>
              )}

              {generatedContent.length > 0 && !isEditMode && (
                <>
                  <button
                    onClick={handlePost}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base"
                    disabled={isPublishing || selectedFanpages.length === 0}
                  >
                    {isPublishing ? (
                      <ClipLoader size={20} color={"#fff"} />
                    ) : (
                      <FiSend />
                    )}
                    {isPublishing ? "Đang đăng bài..." : "Đăng bài viết"}
                  </button>
                  <button
                    onClick={() => setShowScheduleModal(true)}
                    className="w-full flex items-center justify-center gap-2 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm sm:text-base mt-2"
                    disabled={isPublishing || isScheduling || selectedFanpages.length === 0}
                  >
                    <FiClock />
                    Lên lịch đăng bài
                  </button>
                  {selectedFanpages.length === 0 && (
                    <p className="text-red-500 text-sm mt-2 text-center">
                      Vui lòng chọn ít nhất một {contentPlatform === "zalo" ? "tài khoản Zalo OA" : "Fanpage"} để đăng bài
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <ToastContainer />

        {/* Danh sách bài viết đã lên lịch */}
        <ScheduledPostsList />

        {/* Schedule Post Modal */}
        <SchedulePostModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onConfirm={schedulePost}
          isScheduling={isScheduling}
        />
      </div>
    </div>
  );
};

export default PhotosPostGenerator;
